import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { api, formatApiError } from "../../pages/Admin";

export const PasswordFields = ({ values, onChange, withCurrent }) => (
  <>
    {withCurrent && (
      <Field id="current" label="Current password" value={values.current} onChange={onChange} auto="current-password" />
    )}
    <Field id="next" label="New password (at least 8 characters)" value={values.next} onChange={onChange} auto="new-password" />
    <Field id="confirm" label="Confirm new password" value={values.confirm} onChange={onChange} auto="new-password" />
  </>
);

const Field = ({ id, label, value, onChange, auto }) => (
  <div>
    <Label htmlFor={`pw-${id}`}>{label}</Label>
    <Input id={`pw-${id}`} type="password" value={value} autoComplete={auto} data-testid={`password-${id}-input`}
      onChange={(e) => onChange(id, e.target.value)} className="mt-2 h-11" />
  </div>
);

export const checkPasswords = ({ next, confirm }) => {
  if (next.length < 8) return "New password must be at least 8 characters";
  if (next !== confirm) return "New passwords don't match";
  return "";
};

const EMPTY = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const onChange = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const problem = checkPasswords(values);
    if (problem) return setError(problem);
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: values.current, new_password: values.next });
      toast({ title: "Password changed", description: "Other devices have been logged out." });
      setOpen(false);
      setValues(EMPTY);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="admin-change-password-btn" className="rounded-full h-9">
        <KeyRound className="h-4 w-4 mr-1.5" /> Change password
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); setError(""); }}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Other devices will be logged out after the change.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <PasswordFields values={values} onChange={onChange} withCurrent />
            {error && <p className="text-sm text-red-600" data-testid="change-password-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="change-password-submit"
              className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white">
              {loading ? "Saving…" : "Save new password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
